import React, { useState } from 'react';
import { Award, HeadphonesIcon, GraduationCap, TrendingUp, Megaphone, Wrench, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { partnerBenefits } from '../data/mockData';

const iconMap = {
  Award,
  HeadphonesIcon,
  GraduationCap,
  TrendingUp,
  Megaphone,
  Wrench
};

export const Partners = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    businessType: '',
    city: '',
    experience: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - in real app this would send to backend
    console.log('Partner application:', formData);
    
    toast({
      title: 'Заявку відправлено!',
      description: 'Ми зв\'яжемося з вами найближчим часом.',
    });
    
    // Reset form
    setFormData({
      companyName: '',
      contactPerson: '',
      phone: '',
      email: '',
      businessType: '',
      city: '',
      experience: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Станьте нашим партнером
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Запрошуємо до ексклюзивної співпраці інсталяторів, проєктантів, девелоперів та власників бізнесу. 
            Пропонуємо технічну підтримку, навчання й вигідні комерційні умови.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Переваги партнерства
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Що ви отримаєте, працюючи з PURA Ukraine
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnerBenefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <Card key={benefit.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                        <Icon className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Кого ми шукаємо
            </h2>
            <p className="text-lg text-gray-600">
              Ми зацікавлені в співпраці з
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Монтажні організації та інсталятори',
              'Проєктні бюро та інженери',
              'Будівельні компанії',
              'Девелопери котеджних містечок',
              'Дистриб\'ютори будівельної техніки',
              'Сервісні центри та дилери'
            ].map((type, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg bg-white hover:shadow-md transition-shadow">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                <span className="text-gray-800 font-medium">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Заявка на партнерство
            </h2>
            <p className="text-lg text-gray-600">
              Заповніть форму, і ми зв\'яжемося з вами для обговорення умов співпраці
            </p>
          </div>
          
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Назва компанії *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder="Ваша компанія"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contactPerson">Контактна особа *</Label>
                    <Input
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      required
                      placeholder="Прізвище та ім\'я"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Телефон *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+380 XX XXX XX XX"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="businessType">Тип бізнесу *</Label>
                    <Select
                      value={formData.businessType}
                      onValueChange={(value) => setFormData({ ...formData, businessType: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Оберіть тип" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="installer">Монтажна організація</SelectItem>
                        <SelectItem value="designer">Проєктне бюро</SelectItem>
                        <SelectItem value="developer">Девелопер</SelectItem>
                        <SelectItem value="distributor">Дистриб'ютор</SelectItem>
                        <SelectItem value="construction">Будівельна компанія</SelectItem>
                        <SelectItem value="service">Сервісний центр</SelectItem>
                        <SelectItem value="other">Інше</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="city">Місто *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      placeholder="Ваше місто"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="experience">Досвід роботи</Label>
                  <Input
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Кількість років досвіду"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Повідомлення</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Розкажіть про вашу компанію та очікування від співпраці..."
                  />
                </div>
                
                <Button
                  type="submit"
                  size="lg"
                  className="w-full text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'rgb(1, 157, 180)' }}
                >
                  Відправити заявку
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};